import { Workbook } from 'exceljs';
import type { Alignment } from 'exceljs';

type FormulaCell = {
	formula: string;
	value?: string | number | boolean | Date;
};

type CellValue = unknown;
type SheetRow = Record<string, unknown>;
type CellDataType = 'string' | 'number' | 'boolean' | 'date';
type NumberFormatResolver = (row: SheetRow) => string | undefined;

export type WorkbookColumn = {
	key: string;
	header?: string;
	type?: CellDataType;
	numFmt?: string | NumberFormatResolver;
};

type WorkbookSheet = {
	name: string;
	rows: SheetRow[];
	columns?: WorkbookColumn[];
	cells?: Record<string, CellValue>;
	dataCellAlignment?: Partial<Alignment>;
};

function isFormulaCell(value: unknown): value is FormulaCell {
	return typeof value === 'object' && value !== null && 'formula' in value;
}

function parseDateOnlyString(value: string): Date | null {
	const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(value.trim());
	if (!match) {
		return null;
	}

	const [, year, month, day] = match;
	return new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
}

function coerceCellValue(value: unknown, type?: CellDataType): unknown {
	if (value === null || value === undefined) {
		return undefined;
	}

	if (isFormulaCell(value)) {
		return {
			formula: value.formula,
			result: value.value ?? 0
		};
	}

	if (type === 'date') {
		if (value instanceof Date) {
			return value;
		}

		if (typeof value === 'string') {
			const parsedDateOnly = parseDateOnlyString(value);
			if (parsedDateOnly) {
				return parsedDateOnly;
			}

			const parsedDate = new Date(value);
			return Number.isNaN(parsedDate.getTime()) ? String(value) : parsedDate;
		}

		if (typeof value === 'number') {
			const parsedDate = new Date(value);
			return Number.isNaN(parsedDate.getTime()) ? value : parsedDate;
		}
	}

	if (type === 'number') {
		if (typeof value === 'number') {
			return value;
		}

		if (typeof value === 'string') {
			const normalizedValue = value.trim();
			if (normalizedValue.length === 0) {
				return undefined;
			}

			const numericValue = Number(normalizedValue);
			return Number.isNaN(numericValue) ? String(value) : numericValue;
		}
	}

	if (type === 'boolean') {
		return Boolean(value);
	}

	if (type === 'string') {
		return String(value);
	}

	return value instanceof Date || typeof value === 'number' || typeof value === 'boolean'
		? value
		: String(value);
}

function getColumnConfigs(rows: SheetRow[], columns?: WorkbookColumn[]): WorkbookColumn[] {
	if (columns?.length) {
		return columns;
	}

	if (!rows.length) {
		return [];
	}

	return Object.keys(rows[0]).map(key => ({ key, header: key }));
}

function bufferToArrayBuffer(buffer: Uint8Array): ArrayBuffer {
	return buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
}

export async function createWorkbookBuffer(sheets: WorkbookSheet[]): Promise<ArrayBuffer> {
	const workbook = new Workbook();

	for (const sheetConfig of sheets) {
		const worksheet = workbook.addWorksheet(sheetConfig.name);
		const columns = getColumnConfigs(sheetConfig.rows, sheetConfig.columns);

		if (columns.length > 0) {
			const headerRow = worksheet.addRow(columns.map(column => column.header ?? column.key));
			headerRow.font = { bold: true };

			sheetConfig.rows.forEach(row => {
				const wsRow = worksheet.addRow(columns.map(column => coerceCellValue(row[column.key], column.type) ?? ''));
				columns.forEach((column, index) => {
					if (sheetConfig.dataCellAlignment) {
						wsRow.getCell(index + 1).alignment = sheetConfig.dataCellAlignment;
					}
					const numFmt = typeof column.numFmt === 'function'
						? column.numFmt(row)
						: column.numFmt;
					if (numFmt) {
						wsRow.getCell(index + 1).numFmt = numFmt;
					}
				});
			});
		}

		// Apply formula and special cells
		if (sheetConfig.cells) {
			Object.entries(sheetConfig.cells).forEach(([address, value]) => {
				const cell = worksheet.getCell(address);

				if (isFormulaCell(value)) {
					cell.value = {
						formula: value.formula,
						result: value.value ?? 0
					};
				} else {
					const cellValue = coerceCellValue(value);
					if (cellValue !== undefined) {
						cell.value = cellValue as string | number | boolean | Date;
					}
				}
			});
		}
	}

	const buffer = await workbook.xlsx.writeBuffer();
	return bufferToArrayBuffer(buffer as Uint8Array);
}
